import React, { useState, useEffect } from 'react';
import { X, Share2, Users, Copy, CheckCircle, ExternalLink, Clock, Shield } from 'lucide-react';
import { api } from '@/utils/api';

interface CrewCertShareModalProps {
  onClose: () => void;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  role: string;
}

export function CrewCertShareModal({ onClose }: CrewCertShareModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/jha/crew-members');
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const toggleUser = (userId: number) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleGenerateLink = async () => {
    if (selectedUsers.length === 0) {
      alert('Please select at least one crew member');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/certifications/share', {
        userIds: selectedUsers,
      });

      const { token, expiresAt: expiry } = response.data.data;
      const link = `${window.location.origin}/cert-share/${token}`;
      
      setShareLink(link);
      setExpiresAt(expiry);
    } catch (error: any) {
      console.error('Error generating share link:', error);
      alert(error.response?.data?.message || 'Failed to generate share link');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenLink = () => {
    if (shareLink) {
      window.open(shareLink, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                <Share2 className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Crew Cert Share™</h2>
                <p className="text-green-100 text-sm">Generate a secure, temporary link for auditors</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-100 transition"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Key Features */}
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-semibold">24-Hour Expiration</span>
              </div>
              <p className="text-xs text-green-100">Link auto-expires for security</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="h-4 w-4" />
                <span className="text-sm font-semibold">No Login Required</span>
              </div>
              <p className="text-xs text-green-100">Auditors view instantly</p>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-semibold">Real-Time Status</span>
              </div>
              <p className="text-xs text-green-100">Shows current cert status</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!shareLink ? (
            <>
              {/* Select Crew Members */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 text-gray-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Select Crew Members</h3>
                  <span className="text-sm text-gray-500">({selectedUsers.length} selected)</span>
                </div>

                <div className="grid md:grid-cols-2 gap-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => toggleUser(user.id)}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition ${
                        selectedUsers.includes(user.id)
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          selectedUsers.includes(user.id)
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {selectedUsers.includes(user.id) ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <Users className="h-4 w-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900 text-sm">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-xs text-gray-600">{user.role}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Select */}
              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => setSelectedUsers(users.map(u => u.id))}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                >
                  Select All
                </button>
                <button
                  onClick={() => setSelectedUsers([])}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition"
                >
                  Clear All
                </button>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Select the crew members you want to share certifications for</li>
                  <li>Click "Generate Share Link" to create a secure, temporary link</li>
                  <li>Share the link with the auditor via text, email, or QR code</li>
                  <li>Auditor views all certifications without needing to log in</li>
                  <li>Link automatically expires after 24 hours for security</li>
                </ol>
              </div>

              {/* Generate Button */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateLink}
                  disabled={generating || selectedUsers.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Share2 className="h-4 w-4" />
                  {generating ? 'Generating...' : 'Generate Share Link'}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Share Link Generated!</h3>
                <p className="text-gray-600">
                  Share this link with the auditor. It will expire in 24 hours.
                </p>
              </div>

              {/* Link Display */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Shareable Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-mono"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    onClick={handleCopyLink}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Expiration Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-yellow-900 mb-1">Link Expires:</h4>
                    <p className="text-sm text-yellow-800">
                      {expiresAt ? new Date(expiresAt).toLocaleString() : '24 hours from now'}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      After expiration, you'll need to generate a new link for security reasons.
                    </p>
                  </div>
                </div>
              </div>

              {/* Selected Crew Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">Included Crew Members:</h4>
                <div className="flex flex-wrap gap-2">
                  {users
                    .filter(u => selectedUsers.includes(u.id))
                    .map(user => (
                      <span
                        key={user.id}
                        className="px-3 py-1 bg-white border border-gray-300 rounded-full text-sm text-gray-700"
                      >
                        {user.firstName} {user.lastName}
                      </span>
                    ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Close
                </button>
                <button
                  onClick={handleOpenLink}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Preview Link
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

